import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { LoadingSpinner } from '@/components/ui'
import {
  RefreshCw, FileText, CheckCircle, AlertTriangle, XCircle, MinusCircle,
  ExternalLink, ChevronRight, ChevronDown, Info,
} from 'lucide-react'

interface DocRow {
  document: string
  issues: boolean
  frequency: string | null
  last_version: string | null
  status: 'ok' | 'behind' | 'missing' | 'irregular' | 'suppressed' | 'not_issued'
  months_behind: number | null
  drive_link: string | null
  comments: string | null
}
interface FundRow {
  key: string
  fund: string
  manager: string | null
  invested_since: string | null
  main_folder: string | null
  expected: number
  ok: number
  behind: number
  missing: number
  suppressed: number
  irregular: number
  coverage_pct: number | null
  documents: DocRow[]
}
interface Report {
  available: boolean
  message?: string
  last_refreshed: string | null
  as_of?: string
  summary?: {
    funds: number; expected: number; ok: number; behind: number
    missing: number; suppressed: number; irregular: number; coverage_pct: number | null
  }
  funds?: FundRow[]
}

const STATUS_META: Record<string, { label: string; cls: string; Icon: any }> = {
  ok: { label: 'Up to date', cls: 'text-green-700 bg-green-50 border-green-200', Icon: CheckCircle },
  behind: { label: 'Behind', cls: 'text-amber-700 bg-amber-50 border-amber-200', Icon: AlertTriangle },
  missing: { label: 'Missing', cls: 'text-red-700 bg-red-50 border-red-200', Icon: XCircle },
  irregular: { label: 'Ad-hoc', cls: 'text-sky-700 bg-sky-50 border-sky-200', Icon: Info },
  suppressed: { label: 'Waived', cls: 'text-muted-foreground bg-muted border-border', Icon: MinusCircle },
  not_issued: { label: 'Not issued', cls: 'text-muted-foreground bg-muted border-border', Icon: MinusCircle },
}

function covColor(p: number | null): string {
  if (p == null) return 'bg-muted-foreground'
  if (p >= 80) return 'bg-green-500'
  if (p >= 50) return 'bg-amber-500'
  return 'bg-red-500'
}

function StatPill({ status }: { status: string }) {
  const m = STATUS_META[status] || STATUS_META.not_issued
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${m.cls}`}>
      <m.Icon className="h-3 w-3" /> {m.label}
    </span>
  )
}

export default function DocumentCompleteness() {
  const qc = useQueryClient()
  const [open, setOpen] = useState<Record<string, boolean>>({})

  const { data, isLoading } = useQuery<Report>({
    queryKey: ['doc-completeness'],
    queryFn: () => api.get('/docs/completeness').then((r) => r.data),
  })

  const refresh = useMutation({
    mutationFn: () => api.post('/docs/refresh').then((r) => r.data),
    onSuccess: (d) => qc.setQueryData(['doc-completeness'], d),
  })

  const s = data?.summary

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Document Completeness</h1>
          <p className="text-muted-foreground">
            Per-fund document coverage from the Fund Document Tracker
            {data?.as_of && <> · assessed as of <strong>{data.as_of}</strong></>}
          </p>
        </div>
        <button
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2 shrink-0"
        >
          <RefreshCw className={`h-4 w-4 ${refresh.isPending ? 'animate-spin' : ''}`} />
          {refresh.isPending ? 'Refreshing…' : 'Refresh tracker'}
        </button>
      </div>

      {refresh.isError && (
        <div className="flex items-center gap-2 p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
          <AlertTriangle className="h-4 w-4" />
          {(refresh.error as any)?.response?.data?.detail || 'Refresh failed'}
        </div>
      )}

      {isLoading && <LoadingSpinner message="Loading…" />}

      {!isLoading && data && !data.available && (
        <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
          <FileText className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">{data.message || 'No tracker cached yet.'}</p>
          <p className="text-xs mt-1">Click “Refresh tracker” to pull the latest file.</p>
        </div>
      )}

      {!isLoading && data?.available && s && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Kpi label="Coverage" value={s.coverage_pct != null ? `${s.coverage_pct}%` : '—'} accent />
            <Kpi label="Funds" value={String(s.funds)} />
            <Kpi label="Expected docs" value={String(s.expected)} />
            <Kpi label="Up to date" value={String(s.ok)} tone="green" />
            <Kpi label="Behind" value={String(s.behind)} tone="amber" />
            <Kpi label="Missing" value={String(s.missing)} tone="red" />
          </div>

          <p className="text-xs text-muted-foreground">
            Presence is the tracker’s recorded “last version” per document vs its expected frequency
            (+1 month grace), judged as of the tracker’s latest recorded date. Known exceptions on the
            Suppression sheet are waived.
            {data.last_refreshed && <> Tracker last refreshed {new Date(data.last_refreshed).toLocaleString()}.</>}
          </p>

          {/* Per-fund */}
          <div className="space-y-3">
            {data.funds!.map((f) => {
              const isOpen = open[f.key]
              return (
                <div key={f.key} className="bg-card border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpen((o) => ({ ...o, [f.key]: !o[f.key] }))}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/40"
                  >
                    {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{f.fund}</div>
                      <div className="text-xs text-muted-foreground truncate">{f.manager}</div>
                    </div>
                    <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                      {f.behind > 0 && <span className="text-amber-600">{f.behind} behind</span>}
                      {f.missing > 0 && <span className="text-red-600">{f.missing} missing</span>}
                      <span>{f.ok}/{f.expected} ok</span>
                    </div>
                    <div className="w-28 shrink-0">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                          <div className={`h-full ${covColor(f.coverage_pct)}`} style={{ width: `${f.coverage_pct ?? 0}%` }} />
                        </div>
                        <span className="text-xs font-semibold tabular-nums w-9 text-right">
                          {f.coverage_pct != null ? `${f.coverage_pct}%` : '—'}
                        </span>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-border overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-muted-foreground bg-muted/30">
                            <th className="py-2 px-4 font-medium">Document</th>
                            <th className="py-2 px-3 font-medium">Status</th>
                            <th className="py-2 px-3 font-medium">Frequency</th>
                            <th className="py-2 px-3 font-medium">Last version</th>
                            <th className="py-2 px-3 font-medium">Link</th>
                          </tr>
                        </thead>
                        <tbody>
                          {f.documents.map((d, i) => (
                            <tr key={i} className={`border-t border-border ${d.status === 'not_issued' ? 'opacity-50' : ''}`}>
                              <td className="py-2 px-4 font-medium">{d.document}</td>
                              <td className="py-2 px-3"><StatPill status={d.status} /></td>
                              <td className="py-2 px-3 text-muted-foreground">{d.frequency || '—'}</td>
                              <td className="py-2 px-3 tabular-nums">
                                {d.last_version || '—'}
                                {d.months_behind != null && d.status === 'behind' && (
                                  <span className="text-amber-600 text-xs"> ({d.months_behind}m)</span>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                {d.drive_link ? (
                                  <a href={d.drive_link} target="_blank" rel="noopener noreferrer"
                                     className="text-primary hover:underline inline-flex items-center gap-1">
                                    Drive <ExternalLink className="h-3 w-3" />
                                  </a>
                                ) : <span className="text-muted-foreground">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function Kpi({ label, value, tone, accent }: { label: string; value: string; tone?: 'green' | 'amber' | 'red'; accent?: boolean }) {
  const toneCls =
    tone === 'green' ? 'text-green-600' : tone === 'amber' ? 'text-amber-600' : tone === 'red' ? 'text-red-600'
    : accent ? 'text-primary' : ''
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${toneCls}`}>{value}</div>
    </div>
  )
}
